import { Capacitor } from "@capacitor/core";
import { logger } from "@/logger";

/**
 * Opens the native maps application with directions to the specified address or coordinates. On
 * iOS, opens Apple Maps. On Android, opens Google Maps. On web, opens Google Maps in browser.
 *
 * @param destination - The destination address or coordinates
 * @param options - Optional configuration
 */
export async function openNativeNavigation(
  destination: string | { latitude: number; longitude: number },
  options?: {
    /** Travel mode: 'driving' | 'walking' | 'transit' | 'bicycling' */
    mode?: "driving" | "walking" | "transit" | "bicycling";
    /** Label for the destination */
    label?: string;
  },
) {
  const platform = Capacitor.getPlatform();
  const mode = options?.mode || "driving";

  try {
    let url: string;

    if (typeof destination === "string") {
      // Address-based navigation
      const encodedAddress = encodeURIComponent(destination);
      const label = options?.label ? encodeURIComponent(options.label) : encodedAddress;

      if (platform === "ios") {
        // Apple Maps URL scheme
        // Mode mapping: d=driving, w=walking, r=transit (no bicycling support)
        const appleMapsMode = mode === "transit" ? "r" : mode === "walking" ? "w" : "d";
        url = `maps://maps.apple.com/?daddr=${encodedAddress}&dirflg=${appleMapsMode}`;
      } else {
        // Google Maps URL scheme (Android and web)
        const googleMapsMode =
          mode === "bicycling" ? "b" : mode === "transit" ? "r" : mode === "walking" ? "w" : "d";
        url = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=${mode}`;

        // On Android, try to use the app scheme first, fallback to web
        if (platform === "android") {
          url = `google.navigation:q=${encodedAddress}&mode=${googleMapsMode}`;
        }
      }
    } else {
      // Coordinate-based navigation
      const { latitude, longitude } = destination;
      const label = options?.label ? encodeURIComponent(options.label) : `${latitude},${longitude}`;

      if (platform === "ios") {
        // Apple Maps with coordinates
        const appleMapsMode = mode === "transit" ? "r" : mode === "walking" ? "w" : "d";
        url = `maps://maps.apple.com/?daddr=${latitude},${longitude}&dirflg=${appleMapsMode}`;
      } else {
        // Google Maps with coordinates
        const googleMapsMode =
          mode === "bicycling" ? "b" : mode === "transit" ? "r" : mode === "walking" ? "w" : "d";
        url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=${mode}`;

        // On Android, try to use the app scheme first
        if (platform === "android") {
          url = `google.navigation:q=${latitude},${longitude}&mode=${googleMapsMode}`;
        }
      }
    }

    // Open the URL
    if (platform === "web") {
      window.open(url, "_blank");
    } else {
      // Use window.location for native platforms to trigger the URL scheme
      window.location.href = url;
    }

    logger.info("Opened native navigation:", { destination, mode, platform });
  } catch (error) {
    logger.error("Failed to open native navigation:", error);
    throw error;
  }
}

/**
 * Opens the native maps application to show a location (without directions).
 *
 * @param location - The location address or coordinates
 * @param options - Optional configuration
 */
export async function openNativeMaps(
  location: string | { latitude: number; longitude: number },
  options?: {
    /** Label for the location */
    label?: string;
    /** Zoom level (1-20, higher = more zoomed in) */
    zoom?: number;
  },
) {
  const platform = Capacitor.getPlatform();
  const zoom = options?.zoom || 15;

  try {
    let url: string;

    if (typeof location === "string") {
      const encodedAddress = encodeURIComponent(location);
      const label = options?.label ? encodeURIComponent(options.label) : encodedAddress;

      if (platform === "ios") {
        url = `maps://maps.apple.com/?q=${label}&address=${encodedAddress}`;
      } else {
        url = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      }
    } else {
      const { latitude, longitude } = location;
      const label = options?.label ? encodeURIComponent(options.label) : "";

      if (platform === "ios") {
        url = `maps://maps.apple.com/?ll=${latitude},${longitude}&q=${label || "Location"}`;
      } else {
        url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      }
    }

    if (platform === "web") {
      window.open(url, "_blank");
    } else {
      window.location.href = url;
    }

    logger.info("Opened native maps:", { location, platform });
  } catch (error) {
    logger.error("Failed to open native maps:", error);
    throw error;
  }
}
